import { AccountOption, Link } from '@cma/common'
import { useOnboardingForm } from '@cma/pages/sso/SsoOnboarding'
import { HelixIcon } from '@helix/helix-icon'
import { spinner as SpinnerIcon } from '@helix/helix-icon/outlined'
import { yupResolver } from '@hookform/resolvers/yup'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import * as yup from 'yup'
import { useAccountOptions, useLogin } from '../auth'
import { SelectSsoAccountCard } from './SelectSsoAccountCard'

type Step = 'email' | 'select' | 'password'

const schema = yup.object({
  email: yup.string().email('Invalid email').required('Your email is required'),
  password: yup.string().when('$step', {
    is: 'password',
    then: (field) => field.required('Your password is required')
  })
})

export default function SsoLogin() {
  const { service } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token') || ''
  const emailFromQuery = searchParams.get('email') || ''
  const magicLinkError = searchParams.get('error')
    ? 'Magic link is expired or invalid'
    : ''

  const { getValues, setValue: setOnboardingValue } = useOnboardingForm()

  const [step, setStep] = useState<Step>('email')
  const [submittedEmail, setSubmittedEmail] = useState('')
  const [accounts, setAccounts] = useState<AccountOption[]>([])
  const [selectedAccountId, setSelectedAccountId] = useState<string>()

  const { mutate: lookupAccounts, isLoading: isLookingUp } = useAccountOptions()

  const {
    mutate: login,
    isLoading: isLoggingIn,
    error: errorLogginIn,
    reset: resetLogin
  } = useLogin({
    async onSuccess(data) {
      await new Promise(() => {
        if (
          service === 'rdc' &&
          data.authenticate?.user?.features?.offerRdcToolkit
        ) {
          navigate(`/sso/connect/${service}?token=${token}`)
        } else {
          window.location.href = process.env.VITE_APP_CMA_URL
        }
      })
    }
  })

  const {
    watch,
    register,
    handleSubmit,
    setValue,
    formState: { errors, isValid }
  } = useForm<yup.InferType<typeof schema>>({
    resolver: yupResolver(schema),
    context: { step },
    defaultValues: {
      email: emailFromQuery || getValues('email')
    }
  })

  function goToPasswordStep(options: AccountOption[], account?: AccountOption) {
    setOnboardingValue('hideChrome', false)
    setAccounts(options)
    setSelectedAccountId(account?.accountId)
    setStep('password')
  }

  function findAccounts(email: string) {
    setSubmittedEmail(email)
    setAccounts([])
    setSelectedAccountId(undefined)
    resetLogin()

    lookupAccounts(email, {
      onSuccess(options) {
        if (options.length <= 1) {
          goToPasswordStep(options, options[0])
          return
        }

        setAccounts(options)
        setStep('select')
        setOnboardingValue('hideChrome', true)
      }
    })
  }

  function startOver() {
    setOnboardingValue('hideChrome', false)
    setStep('email')
    setAccounts([])
    setSelectedAccountId(undefined)
    resetLogin()
    setValue('password', '')
  }

  if (step === 'select') {
    return (
      <SelectSsoAccountCard
        accounts={accounts}
        email={submittedEmail}
        onContinue={(account) => {
          navigate(
            `/sso/email_login/${service}?${new URLSearchParams({
              token,
              email: submittedEmail,
              accountId: account.accountId,
              accountName: account.name
            }).toString()}`
          )
        }}
        onUseDifferentEmail={startOver}
      />
    )
  }

  return (
    <div className="helix-card w-[400px] max-w-full">
      <form
        className="p-8"
        onSubmit={handleSubmit((data) => {
          if (step === 'password') {
            login({
              email: data.email,
              password: data.password ?? '',
              partnershipLogin: true,
              accountId: selectedAccountId
            })
            return
          }

          findAccounts(data.email)
        })}>
        <div className="mb-4 text-center">
          <h1 className="helix-display-small helix-text-black helix-text--center">
            Sign in to continue
          </h1>
          {step === 'password' && (
            <p className="helix-heading helix-text-gray-600 helix-text--center mt-2">
              Enter your Cloud CMA password
            </p>
          )}
        </div>
        <div className="space-y-4">
          <div>
            <label
              className="helix-body--strong helix-mb-1 helix-d-inline-block helix-text-gray-700"
              htmlFor="email">
              Email
            </label>
            <input
              type="text"
              className="helix-text-input"
              id="email"
              placeholder="emmamckenna@acme.com"
              {...register('email', {
                onChange() {
                  if (step !== 'email') {
                    setStep('email')
                    setAccounts([])
                    setSelectedAccountId(undefined)
                    resetLogin()
                  }
                }
              })}
            />
            {errors.email?.message && (
              <div
                role="alert"
                aria-live="polite"
                className="helix-small helix-text--error helix-mt-1">
                {errors.email.message}
              </div>
            )}
          </div>
          {step === 'password' && (
            <div>
              <label
                className="helix-body--strong helix-mb-1 helix-d-inline-block helix-text-gray-700"
                htmlFor="password">
                Password
              </label>
              <input
                type="password"
                className="helix-text-input"
                id="password"
                {...register('password', {
                  onChange() {
                    if (errorLogginIn) resetLogin()
                  }
                })}
              />
              {errors.password?.message && (
                <div
                  role="alert"
                  aria-live="polite"
                  className="helix-small helix-text--error helix-mt-1">
                  {errors.password.message}
                </div>
              )}
            </div>
          )}
          <button
            className="helix-btn helix-btn--primary helix-w-100-percent"
            disabled={
              step === 'password'
                ? !isValid || isLoggingIn
                : !watch('email') || isLookingUp
            }>
            {!isLoggingIn && !isLookingUp ? (
              step === 'password' ? (
                'Sign In'
              ) : (
                'Continue'
              )
            ) : (
              <span className="mx-auto block h-5 w-5 animate-spin">
                <HelixIcon icon={SpinnerIcon} />
              </span>
            )}
          </button>
          {step === 'password' &&
            (errorLogginIn || (!!magicLinkError && !watch('email'))) && (
              <div className="text-center text-sm text-red-500">
                {magicLinkError || errorLogginIn?.message}
              </div>
            )}
          {step === 'password' && (
            <div className="helix-d-flex helix-flex--column helix-small helix-text--center helix-text-gray-600">
              <strong className="helix-text-gray-600">Forgot Password?</strong>
              <p>
                We can email you a magic link so you can Sign In without typing
                a password.{' '}
                <Link
                  to={`/sso/email_login/${service}?${new URLSearchParams({
                    email: watch('email'),
                    token
                  }).toString()}`}>
                  Get Magic Link
                </Link>
              </p>
            </div>
          )}
        </div>
      </form>
    </div>
  )
}
